import { css, cx } from "@emotion/css";
import { Upload } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { OutlinedTextBlackButton } from "src/components/Default/LekUI/Button";
import { TextField } from "src/components/Default/LekUI/TextField";
import { useModals } from "src/components/Modals/context/ModalProvider";
import { ModalsNames } from "src/components/Modals/context/modals";
import CreatorSkeleton from "src/components/NewComponents/Skeletons/Creator";
import Filter from "../../Filters/DefaultFilters";
import AddFlashcardFilter from "../../Filters/FlashcardsFilters/AddFlashcardFilter";
import AllFlashcardFilter from "../../Filters/FlashcardsFilters/AllFlashcardFilter";
import { PermissionsFilters } from "../../Filters/FlashcardsFilters/PermissionFilter";

interface Flashcard {
	id?: string;
	front: string;
	back: string;
	key?: number;
}

interface DeckDetails {
	permission: string;
	description: string;
	flashcards: Flashcard[];
	tagsIds: string[];
}

interface DeckFiltersProps {
	deckDetails: DeckDetails;
	setData: (data: DeckDetails) => void;
	isLoading: boolean;
	onImportName?: (name: string) => void;
}

const FILTER_IDS = {
	flashcards: 0,
	description: 1,
} as const;

export default function DeckFilters({
	deckDetails,
	setData,
	isLoading,
	onImportName,
}: DeckFiltersProps) {
	const { t } = useTranslation();
	const { openModal } = useModals();
	const [expanded, setExpanded] = useState<number | null>(
		deckDetails.description ? FILTER_IDS.description : null,
	);

	const setSelectedData = (newData: Partial<DeckDetails>) =>
		setData({ ...deckDetails, ...newData });

	const handleChange = (id: number) => {
		setExpanded(expanded === id ? null : id);
	};

	if (isLoading) {
		return <CreatorSkeleton />;
	}

	return (
		<div className="flex flex-col gap-16">
			<Filter
				accordionClassName="border-none"
				accordionSummaryClassName={cx(
					"pl-0",
					css`
					   justify-content: flex-start;

					   > span {
							flex: unset;
							margin-right: 4px !important;	
					   }
				`,
				)}
				accordionDetailsClassName="pl-0"
				expanded={expanded === FILTER_IDS.description}
				onChange={() => handleChange(FILTER_IDS.description)}
				title={t(
					"creators.decks.descriptionToggle",
					"Opis zestawu (opcjonalne)",
				)}
				renderFilterDetails={() => (
					<TextField
						className="w-100"
						value={deckDetails.description}
						onChange={(e) => setSelectedData({ description: e.target.value })}
						label={t(
							"creators.decks.descriptionPlaceholder",
							"Opisz swój zestaw",
						)}
						multiline
						rows={6}
					/>
				)}
			/>

			<div className="flex flex-col gap-8">
				<span className="size-16-light">
					{t("filters.decks.permission.title", "Dostępność zestawu")}
				</span>
				<PermissionsFilters
					className="max-w-600"
					permission={deckDetails.permission}
					setPermission={(permission: string) =>
						setSelectedData({ permission })
					}
				/>
			</div>

			<div className="flex flex-col gap-8">
				<span className="size-16-light">
					{t("creators.decks.importDecks", "Importuj zestawy")}
				</span>
				<div className="flex gap-8">
					<OutlinedTextBlackButton
						className="flex items-center gap-4"
						onClick={() =>
							openModal(ModalsNames.DecksImportModal, {
								onConfirm: (flashcards: Flashcard[], deckName?: string) => {
									setSelectedData({
										flashcards: [
											...(deckDetails.flashcards || []),
											...flashcards.map((f, i) => ({
												...f,
												key: Date.now() + i,
											})),
										],
									});
									if (deckName) {
										onImportName?.(deckName);
									}
								},
							})
						}
					>
						<span>{t("commands.import", "Importuj")}</span>
						<Upload className="icon-super-small" />
					</OutlinedTextBlackButton>
				</div>
			</div>

			<AllFlashcardFilter
				selectedData={deckDetails}
				setSelectedData={setSelectedData}
				expanded={expanded === FILTER_IDS.flashcards}
				onChange={() => handleChange(FILTER_IDS.flashcards)}
			/>

			<div className="flex flex-col gap-8">
				<span className="size-16-light">
					{t(
						"creators.decks.addOwnFlashcard",
						"Dodaj własną fiszkę do zestawu",
					)}
				</span>
				<AddFlashcardFilter
					selectedData={deckDetails}
					setSelectedData={setSelectedData}
				/>
			</div>
		</div>
	);
}
